import styled from "@emotion/styled";
import { Form, InputGroup } from "react-bootstrap";
import { FieldErrors, FieldPath, FieldValues, UseFormRegister } from "react-hook-form";

const InputWrapper = styled(Form.Group, { shouldForwardProp: (prop) => prop !== "noMargin" })<{
  noMargin?: boolean;
}>`
  position: relative;

  display: block;
  margin-bottom: ${({ noMargin }) => (noMargin ? 0 : "1rem")};
`;

const StyledCheckbox = styled(Form.Check)`
  max-width: 100%;
`;

interface Props<
  TFieldValues extends FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  name: TFieldName;
  label?: React.ReactNode;
  register: UseFormRegister<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  value?: string | number | string[];

  noMargin?: boolean;
  absoluteFeedback?: boolean;
}

const Checkbox = <
  TFieldValues extends FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "size" | "value" | "placeholder" | "prefix" | "type"
  > &
    Props<TFieldValues, TFieldName>,
) => {
  const { name, label, noMargin, register, errors, ...inputProps } = props;

  const additionalClassNames = errors[name]?.message ? "is-invalid" : "";
  const className = inputProps.className
    ? `${inputProps.className} ${additionalClassNames}`
    : additionalClassNames;

  return (
    <InputWrapper controlId={name} noMargin={noMargin}>
      <InputGroup className={additionalClassNames}>
        <StyledCheckbox
          {...register(name)}
          {...inputProps}
          className={className}
          type="checkbox"
          label={label}
        />
      </InputGroup>

      <Form.Control.Feedback
        type="invalid"
        data-testid={"data-testid" in props ? `${props["data-testid"]}-form-error` : undefined}
      >
        {(errors[name]?.message as string) ?? ""}
      </Form.Control.Feedback>
    </InputWrapper>
  );
};

export default Checkbox;
